import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { selectSelectedEmployersBusinessIds } from 'scenes/WorkDemo/store/selectors/workSearchParamsSelectors';
import {
  selectProjects,
  selectSelectedProject,
} from 'store/projects/selectors/projectsSelectors';
import { selectUserActiveEmployerId } from 'store/user/selectors.js/user.selectors';
import { closeReportSendDialog } from '../actions/reports.actions';
import {
  selectReportBasicData,
  selectReportUserRowTotals,
  selectSeperateAdditionalUserSalaryRows,
} from '../selectors/reports.selectors';
import { postSentraSalaryRowsV2 } from '../services/postSentraSalaryRowsV2.service';
import { returnSentReportDataToSave } from '../utils/returnSentReportDataToSave';
import { pollSalaryTransferStatus } from './pollSalaryTransferStatus.thunk';

export const sendJydacomSalaryDataV2 = createAsyncThunk(
  'reports/sendJydacomSalaryDataV2',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const workCalendarSelectedBusinessIds =
        selectSelectedEmployersBusinessIds(getState());

      const activeEmployerId = selectUserActiveEmployerId(getState());

      const projects = selectProjects(getState());

      const selectedProject = selectSelectedProject(getState());

      const navigationActiveEmployer = projects[activeEmployerId];

      const seperateAdditionalUserSalaryRows =
        selectSeperateAdditionalUserSalaryRows(getState());

      const reportUserRowTotals = selectReportUserRowTotals(getState());

      const reportBasicData = selectReportBasicData(getState());

      let businessId;

      if (workCalendarSelectedBusinessIds[0]) {
        businessId = workCalendarSelectedBusinessIds[0];
      } else if (navigationActiveEmployer) {
        businessId = navigationActiveEmployer.business_id;
      } else {
        businessId = selectedProject.business_id;
      }

      let userSalaryRows = [];

      Object.values(reportUserRowTotals).forEach((row) => {
        const { salaryType, name, userId, personNumber, eachDayTotalHours } =
          row;

        const isMonthlySalaryRow = salaryType === 'WORK_SALARY_MONTHLY';

        if (!isMonthlySalaryRow && eachDayTotalHours) {
          eachDayTotalHours.forEach((dayTotals) => {
            const { day, eachAssignmentHours } = dayTotals;

            Object.values(eachAssignmentHours).forEach((assignmentHourData) => {
              const {
                hours,
                assignmentId,
                jydacomProjectNumber,
                jydacomAssignmentCode,
                jydacomAssignmentId,
                assignmentNumber,
                reductions,
                projectName,
              } = assignmentHourData;

              const hoursWithReduction =
                reductions.AUTOMATIC_MEAL_BREAK_REDUCTION &&
                reductions.AUTOMATIC_MEAL_BREAK_REDUCTION > 0
                  ? hours - reductions.AUTOMATIC_MEAL_BREAK_REDUCTION
                  : hours;

              userSalaryRows.push({
                jydacomProjectNumber,
                jydacomAssignmentCode,
                jydacomAssignmentId,
                projectName,
                userId,
                salaryType,
                unitCount: hoursWithReduction,
                assignmentNumber: assignmentNumber,
                assignmentId: assignmentId,
                date: dayjs(day).set('hour', 12).utc().format(),
                personName: name,
                personNumber,
              });
            });
          });
        }
      });

      Object.values(seperateAdditionalUserSalaryRows).map((userRows) =>
        userRows.forEach((salaryRow) => {
          const {
            unitCount,
            salaryType,
            unitPrice,
            date,
            userId,
            jydacomProjectNumber,
            jydacomAssignmentCode,
            jydacomAssignmentId,
          } = salaryRow;

          const manuallyAddedMainSalaryRow = Object.values(
            Object.values(reportUserRowTotals)
          ).find((userRow) => userRow.userId === salaryRow.userId);

          userSalaryRows.push({
            userId: userId,
            salaryType,
            unitCount,
            unitPrice,
            date: dayjs(date).set('hour', 12).utc().format(),
            personName: salaryRow.userName,
            personNumber: manuallyAddedMainSalaryRow?.personNumber,
            jydacomProjectNumber,
            jydacomAssignmentCode,
            jydacomAssignmentId,
          });
        })
      );

      const reportDataToSave = returnSentReportDataToSave({
        selectedProjectId: selectedProject.id,
        seperateAdditionalUserSalaryRows: seperateAdditionalUserSalaryRows,
        reportBasicData,
        state: 'SENT',
        reportUserRowTotals,
      });

      const response = await postSentraSalaryRowsV2(
        {
          salaryRows: userSalaryRows,
          reportDataToSave,
        },
        businessId
      );

      const reportId = response?.reportId;

      if (reportId) {
        dispatch(
          pollSalaryTransferStatus({
            projectId: selectedProject.id,
            reportId,
          })
        );
      }

      dispatch(closeReportSendDialog());

      return { reportId, businessId };
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  }
);
